import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, TooltipHost } from '@fluentui/react';
import { Glyphicon } from '@bingads-webui-react/primitive-unthemed';
import { MultiStackedBarChart } from '@fluentui/react-charting';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { formatMetric } from '@bingads-webui-campaign-legacy/performance-metric-formatter/index';
import { metrics } from '../constants/metrics-data';
import { Metric } from '../utils/metric-formatter';
import { computerColor, tabletColor, mobileColor, connectedTVColor } from '../constants/device-color';
import { mapSegmentData } from '../utils/metric-mapper';
import { DeviceCardProps } from '../utils/types';
import { renderCustomCallout } from '../helpers/default-card-custom-callout';
import { dropdownCustomStyles } from '../styles/dropdown-styles';
import { getStyles } from '../styles/devices-default-card';

export function DeviceDefaultCard({
  i18n, deviceData, dateRange, allMetrics, initialSelectedMetrics, patchPreference, hideCurrency, footerVisible,
}: DeviceCardProps) {
  const chartPoints = useMemo(() => {
    const chartPointsTemplate = (metric, dateRange, total) => ({
      chartData: [
        {
          legend: _TL_('Computer'), data: 0, color: computerColor, legendColor: computerColor, metric, dateRange, total, metricValue: '0', callOutAccessibilityData: { ariaLabel: 'Computer' }, displayName: allMetrics.find(m => m.key === metric)?.displayName || metric,
        },
        {
          legend: _TL_('Tablet'), data: 0, color: tabletColor, legendColor: tabletColor, metric, dateRange, total, metricValue: '0', callOutAccessibilityData: { ariaLabel: 'Tablet' }, displayName: allMetrics.find(m => m.key === metric)?.displayName || metric,
        },
        {
          legend: _TL_('Smartphone'), data: 0, color: mobileColor, legendColor: mobileColor, metric, dateRange, total, metricValue: '0', callOutAccessibilityData: { ariaLabel: 'Smartphone' }, displayName: allMetrics.find(m => m.key === metric)?.displayName || metric,
        },
        {
          legend: _TL_('CTV'), data: 0, color: connectedTVColor, legendColor: connectedTVColor, metric, dateRange, total, metricValue: '0', callOutAccessibilityData: { ariaLabel: 'ConnectedTV' }, displayName: allMetrics.find(m => m.key === metric)?.displayName || metric,
        },
      ],
    });

    return metrics.reduce((metricChartPointsMap, metric) => ({
      ...metricChartPointsMap,
      [metric]: [chartPointsTemplate(metric, dateRange, 0)],
    }), {});
  }, [dateRange, allMetrics]);

  const data = useMemo(() => {
    // where deviceData is an array
    if (Array.isArray(deviceData)) {
      return deviceData.flatMap(item =>
        // For each device in the array, check if it has `SegmentedData` within `PerformanceMetrics`.
        (item.PerformanceMetrics?.SegmentedData ? mapSegmentData(item.PerformanceMetrics.SegmentedData) : []));

    // deviceData contains a `DeviceSegmentedPerformanceData` object with `PerformanceMetrics`.
    } else if (deviceData?.DeviceSegmentedPerformanceData?.PerformanceMetrics?.SegmentedData) {
      return mapSegmentData(deviceData.DeviceSegmentedPerformanceData.PerformanceMetrics.SegmentedData);

    // deviceData has `PerformanceMetrics` directly without segmented data.
    } else if (deviceData?.PerformanceMetrics) {
      return mapSegmentData(deviceData.PerformanceMetrics.SegmentedData || []);
    }

    return [];
  }, [deviceData]);

  const deviceTypeMap = useMemo(() => data.reduce((deviceMetricsMap, item) => {
    const deviceType = item.deviceType || '';
    return {
      ...deviceMetricsMap,
      [deviceType.toLowerCase()]: item,
    };
  }, {} as Record<string, any>), [data]);

  const overallTotals = useMemo(() => {
    if (Array.isArray(deviceData) && deviceData.length > 0) {
      return deviceData[0]?.PerformanceMetrics || {};
    } else if (deviceData?.PerformanceMetrics) {
      return deviceData.PerformanceMetrics;
    }
    return deviceData?.OverallTotals?.PerformanceMetrics || {};
  }, [deviceData]);

  const updatedChartPoints = useMemo(() => {
    const updatedPoints = { ...chartPoints };

    Object.keys(updatedPoints).forEach((metric) => {
      const typedMetric = metric as Metric;

      updatedPoints[typedMetric][0].chartData = updatedPoints[typedMetric][0].chartData.map((chartData) => {
        const deviceMetrics = deviceTypeMap[chartData.legend.toLowerCase()];

        const value = deviceMetrics && deviceMetrics[typedMetric] !== null && deviceMetrics[typedMetric] !== undefined
          ? deviceMetrics[typedMetric]
          : 0;
        const overallValue = overallTotals[typedMetric] !== null && overallTotals[typedMetric] !== undefined
          ? overallTotals[typedMetric]
          : 1;

        const total = (overallValue === 0) ? 0 : ((value / overallValue) * 100).toFixed(2);

        return {
          ...chartData,
          data: value,
          total,
          metricValue: formatMetric(typedMetric, value),
        };
      });
    });

    return updatedPoints;
  }, [chartPoints, deviceTypeMap, overallTotals]);

  const [selectedViews, setSelectedViews] = useState(initialSelectedMetrics.map(metric => metric.key));

  const dropdownOptions = useMemo(
    () =>
      selectedViews.map((_, index) => {
        const remainingMetrics = allMetrics.filter(metric => !selectedViews.includes(metric.key) || metric.key === selectedViews[index]);
        return {
          key: selectedViews[index],
          text: remainingMetrics.find(m => m.key === selectedViews[index])?.displayName || '',
          options: remainingMetrics.map(metric => ({
            key: metric.key,
            text: metric.displayName,
          })),
        };
      }),
    [selectedViews, allMetrics]
  );
  const styles = useDefaultStyles(() => getStyles(footerVisible));

  const dataViews = selectedViews.map(view => updatedChartPoints[view]);

  const handleViewChange = useCallback(index => (event, option) => {
    setSelectedViews((prevSelectedViews) => {
      const newViews = [...prevSelectedViews];
      newViews[index] = option.key;
      patchPreference({ selectedColumns: newViews });
      return newViews;
    });
  }, [patchPreference]);

  return (
    <div className={classNames(styles.deviceDefaultCard, 'ba-device-default-card')}>
      {dropdownOptions.map((option, index) => (
        <React.Fragment key={option.key}>
          <div className={classNames(`ba-device-default-dropdown-${index}`, styles.dropdownDefaultCard)}>
            <Dropdown
              selectedKey={selectedViews[index]}
              onChange={handleViewChange(index)}
              options={option.options}
              styles={dropdownCustomStyles}
              aria-label={i18n.getString(_TL_('Select view for option {{option}}'), { option: option.key })}
            />
            {hideCurrency && (selectedViews[index] === 'AverageCPC' || selectedViews[index] === 'Spend' || selectedViews[index] === 'PartialConversionCPA' || selectedViews[index] === 'AdvertiserReportedRevenue') && (
              <TooltipHost
                content={i18n.getString(_TL_('Value is not shown because you have accounts with different currencies.'))}
              >
                <Glyphicon glyph="ba-Info" className="metric-info" />
              </TooltipHost>
            )}
          </div>
          <div className={classNames(`ba-device-default-bar-chart-${index}`, styles.multiStackedChart)}>
            <MultiStackedBarChart
              culture={window.navigator.language}
              data={dataViews[index]}
              hideLegend
              onRenderCalloutPerDataPoint={data => renderCustomCallout(data, styles, dateRange, i18n)}
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
